import React, { FC, memo, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Button } from './button';

const CopyButton: FC<CopyButtonPropsType> = memo(({ value, text, title, isLoading }) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const copyDelayMs = 5 * 1000;

  const [isCopied, setIsCopied] = useState(false);

  useEffect(() => {
    if (!isCopied) {
      return;
    }
    setTimeout(() => setIsCopied(false), copyDelayMs);
  }, [copyDelayMs, isCopied]);

  const copyFromElementFallback = () => {
    if (!inputRef.current) {
      return false;
    }

    const inputDOM = inputRef.current;

    try {
      inputDOM.focus();
      inputDOM.select();
      inputDOM.setSelectionRange(0, inputDOM.value.length);
      return document.execCommand('copy');
    } catch {
      return false;
    }
  };

  const copyViaTemporaryTextarea = () => {
    const textArea = document.createElement('textarea');
    textArea.value = value;
    textArea.setAttribute('readonly', '');
    textArea.style.position = 'fixed';
    textArea.style.top = '0';
    textArea.style.left = '0';
    textArea.style.opacity = '0';
    textArea.style.pointerEvents = 'none';
    document.body.appendChild(textArea);

    let isCopiedSuccessfully = false;
    try {
      textArea.focus();
      textArea.select();
      textArea.setSelectionRange(0, textArea.value.length);
      isCopiedSuccessfully = document.execCommand('copy');
    } catch {
      isCopiedSuccessfully = false;
    } finally {
      document.body.removeChild(textArea);
    }

    return isCopiedSuccessfully;
  };

  const copyToClipboard = async (e: any) => {
    if (e) {
      e.preventDefault();
    }

    let isCopiedSuccessfully = false;

    if (navigator?.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(value);
        isCopiedSuccessfully = true;
      } catch {
        isCopiedSuccessfully = false;
      }
    }

    if (!isCopiedSuccessfully) {
      isCopiedSuccessfully = copyFromElementFallback() || copyViaTemporaryTextarea();
    }

    if (isCopiedSuccessfully) {
      setIsCopied(true);
    }
  };

  const btnText = isCopied ? t('Share.Copied') : text;
  return (
    <>
      <Text ref={inputRef} tabIndex={-1} value={value} readOnly />
      <Button
        key="big"
        copy={!btnText}
        type="button"
        onClick={(e) => copyToClipboard(e)}
        title={title}
        disabled={isLoading || isCopied}
      >
        {btnText}
      </Button>
    </>
  );
});

const Text = styled.textarea`
  width: 0;
  height: 0;
  opacity: 0;
  flex: 0;
`;

export default CopyButton;

type CopyButtonPropsType = {
  value: string;
  title?: string;
  text?: string;
  isLoading?: boolean;
};
